import { useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { getServices } from '../services/general';
import { getCookie } from '../services/cookies';
import { message } from 'antd';
import { evaluateConditionals } from '../services/admission';
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, hasEnoughSearchLength } from '../utils/searchConfig';

const getLegendClass = (type) => {
  if (!type) return 'legend-dot--protocol';
  const lowerType = String(type).toLowerCase();
  if (lowerType.includes('battery') || lowerType.includes('bateria')) return 'legend-dot--battery';
  if (lowerType.includes('exam') || lowerType.includes('examen')) return 'legend-dot--exam';
  if (lowerType.includes('collection') || lowerType.includes('protocol')) return 'legend-dot--protocol';
  return 'legend-dot--protocol';
};

const formatPrestation = (serviceData, companyReportType) => ({
  id: serviceData.id,
  name: serviceData.name || serviceData.children,
  type: serviceData.type,
  exam_type: serviceData.exam_type ?? null,
  sale_amount: serviceData.sale_amount,
  original_sale_amount: serviceData.original_sale_amount !== undefined ? serviceData.original_sale_amount : serviceData.sale_amount,
  price_fallback: false,
  currency_id: serviceData.currency_id,
  department_id: serviceData.department_id,
  consents: serviceData.consents || [],
  medical_form_type: serviceData.medical_form_type ?? null,
  battery_exams: serviceData.battery_exams || [],
  report_type: serviceData.report_type ?? companyReportType ?? null,
  additionals: (serviceData.additionals || []).filter((additional, index, self) =>
    index === self.findIndex(existingAdditional => existingAdditional.id === additional.id)
  ),
  removedExamIds: serviceData.removedExamIds || [],
  _originalAdditionalIds: serviceData._originalAdditionalIds
    || (serviceData.additionals || []).map(additional => additional.id),
  availableKeywords: serviceData.availableKeywords || [],
  selectedKeywords: serviceData.selectedKeywords || [],
  conditionalExams: serviceData.conditionalExams || [],
  observations: serviceData.observations || null,
});

const recalculateSaleAmount = (prestation) => {
  const lowerType = String(prestation.type).toLowerCase();
  if (
    !lowerType.includes('battery') &&
    !lowerType.includes('bateria') &&
    !lowerType.includes('collection') &&
    !lowerType.includes('protocol')
  ) {
    return prestation;
  }

  const baseExams = (prestation.battery_exams || []).filter(
    (exam) => !prestation.removedExamIds.includes(exam.id)
  );
  const removedExams = (prestation.battery_exams || []).filter(
    (exam) => prestation.removedExamIds.includes(exam.id)
  );
  const additionals = prestation.additionals || [];

  let baseExamsMissingPrice = false;
  let baseExamsTotal = 0;

  for (const item of baseExams) {
    if (item.sale_amount === null || item.sale_amount === undefined || item.sale_amount === '') {
      baseExamsMissingPrice = true;
    } else {
      const price = item.net_amount !== undefined ? parseFloat(item.net_amount) : parseFloat(item.sale_amount);
      baseExamsTotal += price || 0;
    }
  }

  let removedExamsTotal = 0;
  for (const item of removedExams) {
    if (item.sale_amount != null && item.sale_amount !== '') {
      const price = item.net_amount !== undefined ? parseFloat(item.net_amount) : parseFloat(item.sale_amount);
      removedExamsTotal += price || 0;
    }
  }

  let additionalsTotal = 0;
  for (const item of additionals) {
    const price = item.net_amount !== undefined ? parseFloat(item.net_amount) : parseFloat(item.sale_amount);
    additionalsTotal += price || 0;
  }

  const finalBasePrice = baseExamsMissingPrice
    ? ((parseFloat(prestation.original_sale_amount) || 0) - removedExamsTotal)
    : baseExamsTotal;

  const totalSum = finalBasePrice + additionalsTotal;

  return { ...prestation, sale_amount: totalSum, price_fallback: baseExamsMissingPrice };
};

const extractKeywords = (exam) => {
  if (!exam.conditionals || !Array.isArray(exam.conditionals) || exam.conditionals.length === 0) {
    return [];
  }

  let allTags = new Set();
  for (const rule of exam.conditionals) {
    if (rule.others && Array.isArray(rule.others)) {
      rule.others.forEach(otherTag => allTags.add(otherTag.tag));
    }
  }

  return Array.from(allTags);
};

const usePrestationsEditor = ({
  branchOfficeId,
  companyReportType,
  companyId,
  outsourcingId,
  thirdPartyId,
  evaluationType,
  patientData,
  addAttachedServices = false
} = {}) => {
  const [selectedPrestations, setSelectedPrestations] = useState([]);
  const [activePrestationKey, setActivePrestationKey] = useState(null);
  const [servicesList, setServicesList] = useState([]);
  const [availableExams, setAvailableExams] = useState([]);
  const [loading, setLoading] = useState({
    service: false,
    exam: false,
    conditional: false,
  });

  const resolvedBranchOfficeId = branchOfficeId || getCookie('branch_office_id');

  const activePrestation = selectedPrestations.find(
    (prestation) => `${prestation.id}-${prestation.type}` === activePrestationKey
  );

  const seedPrestations = useCallback(async (prestations) => {
    const evaluatedPrestations = await Promise.all(prestations.map(async (prestation) => {
      let enrichedPrestation = { ...prestation };

      if (enrichedPrestation.battery_exams && enrichedPrestation.battery_exams.length > 0) {
        const availableKeywords = new Set();
        const conditionalExams = [];

        enrichedPrestation.battery_exams.forEach(exam => {
          const tags = extractKeywords(exam);
          tags.forEach(tag => availableKeywords.add(tag));

          if (exam.conditionals && exam.conditionals.length > 0) {
            conditionalExams.push(exam.id);
          }
        });

        enrichedPrestation.availableKeywords = Array.from(availableKeywords);
        enrichedPrestation.conditionalExams = conditionalExams;

        if (!enrichedPrestation.removedExamIds || enrichedPrestation.removedExamIds.length === 0) {
          try {
            const evalResult = await evaluateConditionals({
              patientData,
              batteryExams: enrichedPrestation.battery_exams,
              selectedKeywords: enrichedPrestation.selectedKeywords || []
            });
            enrichedPrestation.removedExamIds = evalResult.removedExamIds || [];
          } catch (err) {
            console.error('Failed to evaluate conditionals', err);
          }
        }
      }

      return enrichedPrestation;
    }));

    const enriched = evaluatedPrestations.map(prestation => recalculateSaleAmount(formatPrestation(prestation, companyReportType)));
    setSelectedPrestations(enriched);
    if (enriched.length > 0) {
      setActivePrestationKey(`${enriched[0].id}-${enriched[0].type}`);
    }
  }, [companyReportType, patientData]);

  const fetchServices = debounce(async (query) => {
    if (!query || !hasEnoughSearchLength(query)) { setServicesList([]); return; }
    setLoading(loading => ({ ...loading, service: true }));
    try {
      const response = await getServices({
        query,
        branch_office_id: resolvedBranchOfficeId,
        company_id: companyId,
        outsourcing_id: outsourcingId,
        third_party_id: thirdPartyId,
        evaluation_type: evaluationType,
      });
      setServicesList(response.data || response);
    } catch (e) {
      console.error(e);
      message.error({
        content: "Error al obtener los servicios",
        style: { marginTop: "45vh" },
      });
    } finally {
      setLoading(loading => ({ ...loading, service: false }));
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS);

  const fetchExams = debounce(async (query, departmentId) => {
    if (!query || !hasEnoughSearchLength(query)) { setAvailableExams([]); return; }
    setLoading(loading => ({ ...loading, exam: true }));
    try {
      const params = {
        query,
        branch_office_id: resolvedBranchOfficeId,
        type: 'Exam',
      };
      if (departmentId) {
        params.department_id = departmentId;
      }
      const response = await getServices(params);
      setAvailableExams(response.data || response);
    } catch (e) {
      console.error(e);
      message.error({
        content: "Error al obtener los exámenes",
        style: { marginTop: "45vh" },
      });
    } finally {
      setLoading(loading => ({ ...loading, exam: false }));
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS);

  const onAddPrestation = async (option) => {
    const serviceData = option.serviceData || option;
    if (!serviceData) return;

    const key = `${serviceData.id}-${serviceData.type}`;
    if (selectedPrestations.some(prestation => `${prestation.id}-${prestation.type}` === key)) {
      return false;
    }

    if (serviceData.battery_exams) {
      const availableKeywords = new Set();
      const conditionalExams = [];

      serviceData.battery_exams?.forEach(exam => {
        const tags = extractKeywords(exam);
        tags.forEach(t => availableKeywords.add(t));

        if (exam.conditionals && exam.conditionals.length > 0) {
          conditionalExams.push(exam.id);
        }
      });

      try {
        const evalResult = await evaluateConditionals({
          patientData,
          batteryExams: serviceData.battery_exams,
          selectedKeywords: []
        });
        serviceData.removedExamIds = evalResult.removedExamIds || [];
      } catch (err) {
        console.error('Failed to evaluate conditionals', err);
        serviceData.removedExamIds = [];
      }

      serviceData.availableKeywords = Array.from(availableKeywords);
      serviceData.conditionalExams = conditionalExams;
    }

    const existingActiveExamIds = new Set();
    selectedPrestations.forEach(prestation => {
      if (prestation.type === 'Exam') {
        existingActiveExamIds.add(prestation.id);
      } else {
        prestation.additionals?.forEach(additional => existingActiveExamIds.add(additional.id));
        prestation.battery_exams?.forEach(batteryExam => {
          if (!prestation.removedExamIds?.includes(batteryExam.id)) {
            existingActiveExamIds.add(batteryExam.id);
          }
        });
      }
    });

    if (serviceData.type === 'Exam') {
      if (existingActiveExamIds.has(serviceData.id)) {
        message.warning(`El examen ${serviceData.name} ya se encuentra en el listado.`);
        setServicesList([]);
        return false;
      }
    }

    const enriched = recalculateSaleAmount(formatPrestation(serviceData, companyReportType));
    const toAdd = [enriched];

    if (addAttachedServices && serviceData.attached_services?.length > 0) {
      serviceData.attached_services.forEach(attached => {
        const attachedKey = `${attached.id}-${attached.type}`;
        const alreadySelected = selectedPrestations.some(prestation => `${prestation.id}-${prestation.type}` === attachedKey);
        const alreadyInBatch = toAdd.some(prestation => `${prestation.id}-${prestation.type}` === attachedKey);
        if (!alreadySelected && !alreadyInBatch) {
          toAdd.push(recalculateSaleAmount(formatPrestation({
            ...attached,
            currency_id: serviceData.currency_id
          }, companyReportType)));
        }
      });
    }

    setSelectedPrestations(prev => {
      const updated = [...prev, ...toAdd];
      if (!activePrestationKey) setActivePrestationKey(key);
      return updated;
    });
    setServicesList([]);
    return true;
  };

  const onRemovePrestation = (key) => {
    setSelectedPrestations(prev => {
      const updated = prev.filter(prestation => `${prestation.id}-${prestation.type}` !== key);
      if (activePrestationKey === key) {
        setActivePrestationKey(updated.length > 0 ? `${updated[0].id}-${updated[0].type}` : null);
      }
      return updated;
    });
  };

  const onToggleKeyword = async (keyword) => {
    const active = selectedPrestations.find(prestation => `${prestation.id}-${prestation.type}` === activePrestationKey);
    if (!active) return;

    setLoading(loading => ({ ...loading, conditional: true }));

    const newSelectedKeywords = active.selectedKeywords?.includes(keyword)
      ? active.selectedKeywords.filter(selectedKeyword => selectedKeyword !== keyword)
      : [...(active.selectedKeywords || []), keyword];

    let newRemovedExamIds = active.removedExamIds || [];
    if (active.battery_exams) {
      try {
        const response = await evaluateConditionals({
          patientData,
          batteryExams: active.battery_exams,
          selectedKeywords: newSelectedKeywords
        });
        const conditionalRemovedIds = response?.removedExamIds || [];
        const manualAdditionalRemovals = (active.removedExamIds || [])
          .filter(examId => active._originalAdditionalIds?.includes(examId));
        newRemovedExamIds = [...new Set([...conditionalRemovedIds, ...manualAdditionalRemovals])];
      } catch (err) {
        console.error('evaluateConditionals error:', err);
      }
    }

    setSelectedPrestations(prev =>
      prev.map(prestation => {
        if (`${prestation.id}-${prestation.type}` !== activePrestationKey) return prestation;
        return recalculateSaleAmount({
          ...prestation,
          selectedKeywords: newSelectedKeywords,
          removedExamIds: newRemovedExamIds
        });
      })
    );
    setLoading(loading => ({ ...loading, conditional: false }));
  };

  const onSelectActive = (key) => setActivePrestationKey(key);

  const onReportTypeChange = (report_type) => {
    setSelectedPrestations(prev =>
      prev.map(prestation => `${prestation.id}-${prestation.type}` === activePrestationKey ? { ...prestation, report_type } : prestation)
    );
  };

  const onAddAdditional = (examData) => {
    if (!examData) return;
    setSelectedPrestations(prev =>
      prev.map(prestation => {
        if (`${prestation.id}-${prestation.type}` !== activePrestationKey) return prestation;
        const alreadyAdded = prestation.additionals.some(additional => additional.id === examData.id);
        if (alreadyAdded) return prestation;
        const alreadyInBattery = prestation.battery_exams?.some(
          batteryExam => batteryExam.id === examData.id && !prestation.removedExamIds?.includes(batteryExam.id)
        );
        if (alreadyInBattery) return prestation;
        const isBaseExam = prestation.battery_exams?.some(
          batteryExam => batteryExam.id === examData.id
        );
        const newPrestation = {
          ...prestation,
          additionals: [{ id: examData.id, name: examData.name, sale_amount: examData.sale_amount, net_amount: examData.net_amount, department_id: examData.department_id }, ...prestation.additionals],
          removedExamIds: isBaseExam
            ? prestation.removedExamIds
            : prestation.removedExamIds.filter(removedId => removedId !== examData.id),
        };
        return recalculateSaleAmount(newPrestation);
      })
    );
    setAvailableExams([]);
  };

  const onRemoveExam = (examId, isAdditional) => {
    setSelectedPrestations(prev =>
      prev.map(prestation => {
        if (`${prestation.id}-${prestation.type}` !== activePrestationKey) return prestation;
        let newPrestation;
        if (isAdditional) {
          const isOriginalAdditional = prestation._originalAdditionalIds?.includes(examId);
          const isConditional = prestation.conditionalExams.some(conditional => conditional === examId);
          newPrestation = {
            ...prestation,
            additionals: prestation.additionals.filter(additional => additional.id !== examId),
            ...((isOriginalAdditional || isConditional) && { removedExamIds: [...prestation.removedExamIds, examId] }),
          };
        } else {
          newPrestation = { ...prestation, removedExamIds: [...prestation.removedExamIds, examId] };
        }
        return recalculateSaleAmount(newPrestation);
      })
    );
  };

  return {
    selectedPrestations,
    setSelectedPrestations,
    activePrestationKey,
    setActivePrestationKey,
    activePrestation,
    servicesList,
    setServicesList,
    availableExams,
    loading,
    seedPrestations,
    fetchServices,
    fetchExams,
    onAddPrestation,
    onRemovePrestation,
    onSelectActive,
    onReportTypeChange,
    onAddAdditional,
    onRemoveExam,
    onToggleKeyword,
    getLegendClass,
    formatPrestation,
  };
};

export default usePrestationsEditor;
export { getLegendClass, formatPrestation };
